import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import localStorageService from '../../utils/localStorageService';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  CalendarDaysIcon,
  ClockIcon,
  UserGroupIcon,
  BookOpenIcon,
  MapPinIcon,
  UserIcon,
  CurrencyEuroIcon,
  DocumentTextIcon,
  ArchiveBoxIcon,
  PencilSquareIcon,
  ArrowPathIcon,
  XMarkIcon,
  CheckIcon,
  PlusIcon,
  TrashIcon,
  ComputerDesktopIcon,
  AcademicCapIcon,
  LinkIcon
} from '@heroicons/react/24/outline';

// Fonction utilitaire pour calculer la durée entre deux dates en minutes
const calculateDuration = (startDateTime, endDateTime) => {
  if (!startDateTime || !endDateTime) return null;
  
  const start = new Date(startDateTime);
  const end = new Date(endDateTime);
  
  // Vérifier que les dates sont valides
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
  
  // Calculer la différence en millisecondes et la convertir en minutes
  const diffMs = end.getTime() - start.getTime();
  const minutes = Math.round(diffMs / (1000 * 60));
  
  return minutes > 0 ? minutes : null;
};

const TrainingSessionForm = ({ onSuccess, onClose, initialData, isEditing = false }) => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  
  // Fetch courses data with real-time updates using useQuery
  const { data: coursesData = [], refetch: refetchCourses, isLoading: coursesLoading } = useQuery('courses', async () => {
    try {
      // No mock data - fetch only real courses
      console.log('Fetching real courses data...');
      const courses = localStorageService.getAllItems('courses');
      // Ensure we always return an array
      return Array.isArray(courses) ? courses : [];
    } catch (error) {
      console.error('Error fetching courses:', error);
      return [];
    }
  }, {
    staleTime: 0, // Always fetch fresh data
    cacheTime: 0, // Don't cache to ensure real-time updates
  });

  // Refetch courses when component mounts to ensure latest data
  useEffect(() => {
    refetchCourses();
  }, [refetchCourses]);

  // Fetch trainers data 
  const { data: trainersData = [] } = useQuery('trainers', async () => {
    try {
      const trainers = localStorageService.getAllItems('trainers');
      // Ensure we always return an array
      return Array.isArray(trainers) ? trainers : [];
    } catch (error) {
      console.error('Error fetching trainers:', error);
      return [];
    }
  }, {
    staleTime: 0,
    cacheTime: 0,
  });

  // Fetch classrooms data with real-time updates using useQuery
  const { data: classroomsData = [], refetch: refetchClassrooms, isLoading: classroomsLoading } = useQuery('classrooms', async () => {
    try {
      console.log('Fetching latest classrooms data...');
      const classrooms = localStorageService.getAllItems('classrooms');
      // Ensure we always return an array
      return Array.isArray(classrooms) ? classrooms : [];
    } catch (error) {
      console.error('Error fetching classrooms:', error);
      return [];
    }
  }, {
    staleTime: 0, // Always fetch fresh data
    cacheTime: 0, // Don't cache to ensure real-time updates
  });

  // Refetch classrooms when component mounts to ensure latest data
  useEffect(() => {
    refetchClassrooms();
  }, [refetchClassrooms]);

  // Format data for dropdowns with defensive programming
  const modules = React.useMemo(() => {
    if (!Array.isArray(coursesData)) return [];
    return coursesData.map(course => ({
      id: course.id,
      name: course.title,
      price: course.price,
      duration: course.duration,
      description: course.description
    }));
  }, [coursesData]);
    
  const instructors = React.useMemo(() => {
    if (!Array.isArray(trainersData)) return [];
    return trainersData.map(trainer => ({
      id: trainer.id,
      name: trainer.name
    }));
  }, [trainersData]);

  const classrooms = React.useMemo(() => {
    if (!Array.isArray(classroomsData)) return [];
    return classroomsData.map(classroom => ({
      id: classroom.id,
      name: classroom.name,
      capacity: classroom.capacity,
      type: classroom.type,
      floor: classroom.floor,
      isAvailable: classroom.isAvailable,
      equipment: classroom.equipment
    }));
  }, [classroomsData]);

  // États du formulaire
  const [formData, setFormData] = useState({
    title: '',
    moduleId: '',
    moduleName: '',
    description: '',
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    location: '',
    locationId: '',
    capacity: 15,
    price: '',
    instructorId: '',
    instructorName: '',
    prerequisites: '',
    equipment: '',
    resources: [],
    enrolledStudents: []
  });
  
  const [resourceName, setResourceName] = useState('');
  const [resourceType, setResourceType] = useState('document');
  const [resourceUrl, setResourceUrl] = useState('');
  const [errors, setErrors] = useState({});
  
  // Remarque: Les cours (comme modules) et formateurs (trainers) sont maintenant chargés depuis localStorage

  // Initialiser le formulaire avec les données existantes pour l'édition
  useEffect(() => {
    if (initialData && isEditing) {
      setFormData({
        ...initialData,
        startTime: initialData.startTime || '',
        endTime: initialData.endTime || '',
        resources: initialData.resources || []
      });
    }
  }, [initialData, isEditing]);

  // Mutation pour ajouter/modifier une session de formation
  const trainingSessionMutation = useMutation(
    async (sessionData) => {
      try {
        // Créer ou mettre à jour dans localStorage
        let newSession;
        
        if (isEditing && initialData) {
          // Mettre à jour une session existante
          newSession = {
            ...initialData,
            ...sessionData,
          };
          localStorageService.updateItem('sessions', newSession);
        } else {
          // Générer un nouvel ID pour la session
          const sessionId = `SES${Date.now().toString().slice(-4)}`;
          
          // Créer une session avec la structure attendue par le tableau
          newSession = {
            id: sessionId,
            title: sessionData.title,
            teacherId: sessionData.instructorId || 'TEACH001',
            courseId: sessionData.courseId,
            date: sessionData.startDateTime?.split('T')[0] || new Date().toISOString().split('T')[0],
            startTime: sessionData.startTime || '09:00',
            endTime: sessionData.endTime || '17:00',
            classroom: sessionData.classroom || 'Salle A1',
            instructorName: sessionData.instructorName || 'Formateur',
            status: sessionData.status || 'confirmed',
            rate: sessionData.rate || 8000,
            duration: sessionData.duration || 3,
            maxStudents: sessionData.maxStudents || sessionData.capacity || 20,
            enrolledStudents: 0,
            students: [],
            // Garder les données spécifiques à la formation
            ...sessionData,
            // Override with correct structure
            memberId: sessionData.instructorId || 'MEM001',
            memberName: sessionData.instructorName || sessionData.title,
            type: 'formation',
            checkInTime: sessionData.startDateTime,
            checkOutTime: sessionData.endDateTime
          };
          localStorageService.addItem('sessions', newSession);
        console.log('✅ Session added to localStorage:', newSession);
        }
        
        // Ajouter également à training-sessions pour maintenir la compatibilité
        if (isEditing && initialData) {
          localStorageService.updateItem('training-sessions', newSession);
        } else {
          localStorageService.addItem('training-sessions', newSession);
        }
        
        // Invalidate queries to trigger real-time updates
        queryClient.invalidateQueries('sessions');
        queryClient.invalidateQueries('training-sessions');
        
        return {
          message: isEditing 
            ? 'Session de formation mise à jour avec succès' 
            : 'Session de formation créée avec succès',
          trainingSession: newSession
        };
      } catch (error) {
        console.error('Erreur lors de la sauvegarde de la session:', error);
        throw new Error('Erreur lors de la sauvegarde de la session');
      }
    },
    {
      onSuccess: (data) => {
        // Invalider les queries pour forcer le rafraîchissement des données
        queryClient.invalidateQueries('training-sessions');
        queryClient.invalidateQueries('sessions');
        queryClient.invalidateQueries('courses'); // Ensure real-time course data updates
        queryClient.invalidateQueries('classrooms'); // Ensure real-time classroom data updates
        if (onSuccess) onSuccess(data.trainingSession);
      }
    }
  );

  // Gestion du formulaire
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Auto-populate fields when a course is selected
    if (name === 'moduleId') {
      const selectedModule = modules.find(m => m.id === value);
      if (selectedModule) {
        setFormData(prev => ({
          ...prev,
          moduleName: selectedModule.name,
          title: selectedModule.name, // Auto-fill the title with course name
          price: selectedModule.price || prev.price, // Auto-fill price if available
          description: selectedModule.description || prev.description // Auto-fill description if available
        }));
      } else {
        // Clear fields if no module is selected
        setFormData(prev => ({
          ...prev,
          moduleName: '',
          title: '',
          price: '',
          description: ''
        }));
      }
    }

    // Auto-populate fields when a classroom is selected
    if (name === 'locationId') {
      const selectedClassroom = classrooms.find(c => c.id === value);
      if (selectedClassroom) {
        console.log('Selected classroom:', selectedClassroom);
        
        // Safely handle equipment field
        let equipmentString = '';
        if (selectedClassroom.equipment) {
          if (Array.isArray(selectedClassroom.equipment)) {
            equipmentString = selectedClassroom.equipment.join(', ');
          } else if (typeof selectedClassroom.equipment === 'string') {
            equipmentString = selectedClassroom.equipment;
          }
        }

        setFormData(prev => ({
          ...prev,
          location: selectedClassroom.name, // Auto-fill the location with classroom name
          capacity: selectedClassroom.capacity, // Auto-fill capacity with classroom capacity
          equipment: equipmentString || prev.equipment // Auto-fill equipment if available
        }));
      } else {
        // Clear fields if no classroom is selected
        setFormData(prev => ({
          ...prev,
          location: '',
          capacity: 15, // Reset to default capacity
          equipment: ''
        }));
      }
    }
    
    // Mise à jour automatique du nom du formateur
    if (name === 'instructorId') {
      const selectedInstructor = instructors.find(i => i.id === value);
      if (selectedInstructor) {
        setFormData(prev => ({
          ...prev,
          instructorName: selectedInstructor.name
        }));
      }
    }

    // Effacer les erreurs lorsqu'un champ est modifié
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Ajouter une ressource
  const handleAddResource = () => {
    if (!resourceName || !resourceUrl) {
      setErrors(prev => ({
        ...prev,
        resources: 'Le nom et l\'URL de la ressource sont requis'
      }));
      return;
    }
    
    const newResource = {
      id: `RES${Date.now()}`,
      name: resourceName,
      type: resourceType,
      url: resourceUrl
    };
    
    setFormData(prev => ({
      ...prev,
      resources: [...prev.resources, newResource]
    }));
    
    // Réinitialiser le formulaire de ressource
    setResourceName('');
    setResourceUrl('');
    setResourceType('document');
    setErrors(prev => ({
      ...prev,
      resources: ''
    }));
  };

  // Supprimer une ressource
  const handleRemoveResource = (resourceId) => {
    setFormData(prev => ({
      ...prev,
      resources: prev.resources.filter(r => r.id !== resourceId)
    }));
  };

  // Validation du formulaire
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title) newErrors.title = 'Le titre est requis';
    if (!formData.moduleId) newErrors.moduleId = 'Le module est requis';
    if (!formData.startDate) newErrors.startDate = 'La date de début est requise';
    if (!formData.startTime) newErrors.startTime = 'L\'heure de début est requise';
    if (!formData.endDate) newErrors.endDate = 'La date de fin est requise';
    if (!formData.endTime) newErrors.endTime = 'L\'heure de fin est requise';
    if (!formData.locationId) newErrors.locationId = 'La salle est requise';
    if (!formData.instructorId) newErrors.instructorId = 'Le formateur est requis';
    if (formData.capacity <= 0) newErrors.capacity = 'La capacité doit être supérieure à 0';
    
    const startDateTime = new Date(`${formData.startDate}T${formData.startTime}`);
    const endDateTime = new Date(`${formData.endDate}T${formData.endTime}`);
    
    if (endDateTime <= startDateTime) {
      newErrors.endDate = 'La date/heure de fin doit être postérieure à la date/heure de début';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Soumission du formulaire
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    const finalData = {
      ...formData,
      courseId: formData.moduleId, // Map moduleId to courseId for session creation
      startDateTime: `${formData.startDate}T${formData.startTime}`,
      endDateTime: `${formData.endDate}T${formData.endTime}`,
    };
    
    trainingSessionMutation.mutate(finalData);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-3xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6 w-full"
    >
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50 flex items-center">
          <AcademicCapIcon className="h-6 w-6 mr-3" />
          {isEditing ? 'Modifier la Session de Formation' : 'Nouvelle Session de Formation'}
        </h2>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50 hover:bg-slate-100 dark:hover:bg-slate-800/80 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        )}
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Informations générales */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="col-span-1 md:col-span-2">
            <label className="block text-slate-600 dark:text-slate-400 mb-1" htmlFor="moduleId">
              Titre de la formation
            </label>
            <select
              id="moduleId"
              name="moduleId"
              value={formData.moduleId}
              onChange={handleFormChange}
              disabled={coursesLoading}
              className={`w-full px-4 py-2 bg-slate-50 dark:bg-slate-800/80 border ${errors.moduleId ? 'border-red-500' : 'border-slate-200 dark:border-slate-600'} rounded-xl text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${coursesLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <option value="">
                {coursesLoading ? '🔄 Chargement des cours...' : 'Sélectionner un cours'}
              </option>
              {modules.map(module => (
                <option key={module.id} value={module.id}>
                  {module.name} {module.price ? `(${module.price} DA)` : ''}
                </option>
              ))}
            </select>
            {errors.moduleId && (
              <p className="text-red-500 text-sm mt-1">{errors.moduleId}</p>
            )}
          </div>


          <div>
            <label className="block text-slate-600 dark:text-slate-400 mb-1" htmlFor="instructorId">
              Formateur
            </label>
            <select
              id="instructorId"
              name="instructorId"
              value={formData.instructorId}
              onChange={handleFormChange}
              className={`w-full px-4 py-2 bg-slate-50 dark:bg-slate-800/80 border ${errors.instructorId ? 'border-red-500' : 'border-slate-200 dark:border-slate-600'} rounded-xl text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500`}
            >
              <option value="">Sélectionner un formateur</option>
              {instructors.map(instructor => (
                <option key={instructor.id} value={instructor.id}>
                  {instructor.name}
                </option>
              ))}
            </select>
            {errors.instructorId && (
              <p className="text-red-500 text-sm mt-1">{errors.instructorId}</p>
            )}
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-slate-600 dark:text-slate-400 mb-1" htmlFor="description">
            Description
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleFormChange}
            rows="3"
            className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Description détaillée de la formation..."
          ></textarea>
        </div>

        {/* Dates et heures */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-slate-600 dark:text-slate-400 mb-1" htmlFor="startDate">
              Date de début
            </label>
            <input
              id="startDate"
              type="date"
              name="startDate"
              value={formData.startDate}
              onChange={handleFormChange}
              className={`w-full px-4 py-2 bg-slate-50 dark:bg-slate-800/80 border ${errors.startDate ? 'border-red-500' : 'border-slate-200 dark:border-slate-600'} rounded-xl text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
            {errors.startDate && (
              <p className="text-red-500 text-sm mt-1">{errors.startDate}</p>
            )}
          </div>

          <div>
            <label className="block text-slate-600 dark:text-slate-400 mb-1" htmlFor="startTime">
              Heure de début
            </label>
            <input
              id="startTime"
              type="time"
              name="startTime"
              value={formData.startTime}
              onChange={handleFormChange}
              className={`w-full px-4 py-2 bg-slate-50 dark:bg-slate-800/80 border ${errors.startTime ? 'border-red-500' : 'border-slate-200 dark:border-slate-600'} rounded-xl text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
            {errors.startTime && (
              <p className="text-red-500 text-sm mt-1">{errors.startTime}</p>
            )}
          </div>

          <div>
            <label className="block text-slate-600 dark:text-slate-400 mb-1" htmlFor="endDate">
              Date de fin
            </label>
            <input
              id="endDate"
              type="date"
              name="endDate"
              value={formData.endDate}
              onChange={handleFormChange}
              className={`w-full px-4 py-2 bg-slate-50 dark:bg-slate-800/80 border ${errors.endDate ? 'border-red-500' : 'border-slate-200 dark:border-slate-600'} rounded-xl text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
            {errors.endDate && (
              <p className="text-red-500 text-sm mt-1">{errors.endDate}</p>
            )}
          </div>

          <div>
            <label className="block text-slate-600 dark:text-slate-400 mb-1" htmlFor="endTime">
              Heure de fin
            </label>
            <input
              id="endTime"
              type="time"
              name="endTime"
              value={formData.endTime}
              onChange={handleFormChange}
              className={`w-full px-4 py-2 bg-slate-50 dark:bg-slate-800/80 border ${errors.endTime ? 'border-red-500' : 'border-slate-200 dark:border-slate-600'} rounded-xl text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
            {errors.endTime && (
              <p className="text-red-500 text-sm mt-1">{errors.endTime}</p>
            )}
          </div>
        </div>

        {/* Lieu, capacité et prix */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-slate-600 dark:text-slate-400 mb-1" htmlFor="locationId">
              Lieu
            </label>
            <select
              id="locationId"
              name="locationId"
              value={formData.locationId}
              onChange={handleFormChange}
              disabled={classroomsLoading}
              className={`w-full px-4 py-2 bg-slate-50 dark:bg-slate-800/80 border ${errors.locationId ? 'border-red-500' : 'border-slate-200 dark:border-slate-600'} rounded-xl text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${classroomsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <option value="">
                {classroomsLoading ? '🔄 Chargement des salles...' : 'Sélectionner une salle'}
              </option>
              {classrooms.map(classroom => (
                <option key={classroom.id} value={classroom.id}>
                  {classroom.name} - {classroom.type} (Capacité: {classroom.capacity}) {!classroom.isAvailable ? '⚠️ Occupée' : '✅ Disponible'}
                </option>
              ))}
            </select>
            {errors.locationId && (
              <p className="text-red-500 text-sm mt-1">{errors.locationId}</p>
            )}
          </div>

          <div>
            <label className="block text-slate-600 dark:text-slate-400 mb-1" htmlFor="capacity">
              Capacité
            </label>
            <input
              id="capacity"
              type="number"
              name="capacity"
              value={formData.capacity}
              readOnly
              className="w-full px-4 py-2 bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-600 dark:text-slate-400 cursor-not-allowed"
              placeholder="Sélectionnez une salle"
            />
            {errors.capacity && (
              <p className="text-red-500 text-sm mt-1">{errors.capacity}</p>
            )}
          </div>

          <div>
            <label className="block text-slate-600 dark:text-slate-400 mb-1" htmlFor="price">
              Prix (DA)
            </label>
            <input
              id="price"
              type="number"
              name="price"
              value={formData.price}
              onChange={handleFormChange}
              min="0"
              className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="ex: 25000"
            />
          </div>
        </div>

        {/* Prérequis et équipement */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-slate-600 dark:text-slate-400 mb-1" htmlFor="prerequisites">
              Prérequis
            </label>
            <textarea
              id="prerequisites"
              name="prerequisites"
              value={formData.prerequisites}
              onChange={handleFormChange}
              rows="3"
              className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="ex: Connaissance de base en HTML, CSS et JavaScript"
            ></textarea>
          </div>

          <div>
            <label className="block text-slate-600 dark:text-slate-400 mb-1" htmlFor="equipment">
              Équipement requis
            </label>
            <textarea
              id="equipment"
              name="equipment"
              value={formData.equipment}
              onChange={handleFormChange}
              rows="3"
              className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="ex: Ordinateur portable avec environnement de développement installé"
            ></textarea>
          </div>
        </div>

        {/* Ressources */}
        <div>
          <h3 className="text-slate-900 dark:text-slate-50 font-medium mb-3">Ressources pédagogiques</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-slate-600 dark:text-slate-400 mb-1" htmlFor="resourceName">
                Nom
              </label>
              <input
                id="resourceName"
                type="text"
                value={resourceName}
                onChange={(e) => setResourceName(e.target.value)}
                className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="ex: Support de cours PDF"
              />
            </div>
            
            <div>
              <label className="block text-slate-600 dark:text-slate-400 mb-1" htmlFor="resourceType">
                Type
              </label>
              <select
                id="resourceType"
                value={resourceType}
                onChange={(e) => setResourceType(e.target.value)}
                className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="document">Document</option>
                <option value="video">Vidéo</option>
                <option value="exercise">Exercice</option>
                <option value="quiz">Quiz</option>
                <option value="link">Lien externe</option>
              </select>
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-slate-600 dark:text-slate-400 mb-1" htmlFor="resourceUrl">
                URL / Lien
              </label>
              <div className="flex">
                <input
                  id="resourceUrl"
                  type="text"
                  value={resourceUrl}
                  onChange={(e) => setResourceUrl(e.target.value)}
                  className="flex-1 px-4 py-2 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-l-xl text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="ex: https://example.com/cours.pdf"
                />
                <button
                  type="button"
                  onClick={handleAddResource}
                  className="bg-blue-500 text-slate-900 dark:text-slate-50 px-4 py-2 rounded-r-xl hover:bg-blue-600 transition-colors"
                >
                  <PlusIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
          
          {errors.resources && (
            <p className="text-red-500 text-sm mb-2">{errors.resources}</p>
          )}
          
          {/* Liste des ressources */}
          <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl overflow-hidden">
            {formData.resources.length === 0 ? (
              <div className="p-4 text-slate-600 dark:text-slate-400 text-center">
                Aucune ressource ajoutée
              </div>
            ) : (
              <ul className="divide-y divide-slate-200 dark:divide-slate-700">
                {formData.resources.map((resource) => (
                  <li key={resource.id} className="flex justify-between items-center p-3 hover:bg-slate-100/80 dark:bg-slate-800/40">
                    <div className="flex items-center">
                      {resource.type === 'document' && <DocumentTextIcon className="h-5 w-5 text-blue-400 mr-2" />}
                      {resource.type === 'video' && <ComputerDesktopIcon className="h-5 w-5 text-red-400 mr-2" />}
                      {resource.type === 'exercise' && <PencilSquareIcon className="h-5 w-5 text-green-400 mr-2" />}
                      {resource.type === 'quiz' && <DocumentTextIcon className="h-5 w-5 text-amber-400 mr-2" />}
                      {resource.type === 'link' && <LinkIcon className="h-5 w-5 text-purple-400 mr-2" />}
                      <div>
                        <p className="text-slate-900 dark:text-slate-50">{resource.name}</p>
                        <p className="text-slate-600 dark:text-slate-400 text-xs truncate max-w-md">{resource.url}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveResource(resource.id)}
                      className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800/80 rounded-lg transition-colors text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        
        {/* Boutons de soumission */}
        <div className="flex justify-end mt-6 space-x-3">
          <button
            type="button"
            onClick={onClose || onSuccess}
            className="px-5 py-2 bg-slate-50 dark:bg-slate-800/80 text-slate-900 dark:text-slate-50 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={trainingSessionMutation.isLoading}
            className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl hover:from-blue-600 hover:to-purple-600 transition-all flex items-center disabled:opacity-70"
          >
            {trainingSessionMutation.isLoading && (
              <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
            )}
            {isEditing ? 'Mettre à jour' : 'Créer la session'}
          </button>
        </div>
      </form>
    </motion.div>
  );
};

export default TrainingSessionForm;
